import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  type: 'metric' | 'icon';
  content?: string;
  platform?: 'spotify' | 'instagram' | 'tiktok' | 'youtube' | 'apple';
  x: number;
  startY: number;
  delay: number;
  duration: number;
  size: 'sm' | 'md' | 'lg';
}

const baseParticleData = [
  // Growth metrics
  { type: 'metric' as const, content: '↑ 2.5M' },
  { type: 'metric' as const, content: '+156%' },
  { type: 'metric' as const, content: '10K saves' },
  { type: 'metric' as const, content: '↑ 847K' },
  { type: 'metric' as const, content: '+2.3M' },
  { type: 'metric' as const, content: '↑ 94%' },
  { type: 'metric' as const, content: '1M+ reach' },
  { type: 'metric' as const, content: '+412%' },
  { type: 'metric' as const, content: '3.2M plays' },
  { type: 'metric' as const, content: '↑ 5.8M' },
  { type: 'metric' as const, content: '+89K' },
  { type: 'metric' as const, content: '2.1M streams' },
  { type: 'metric' as const, content: '↑ 234%' },
  { type: 'metric' as const, content: '500K+ fans' },
  
  // Social icons
  { type: 'icon' as const, platform: 'spotify' as const },
  { type: 'icon' as const, platform: 'instagram' as const },
  { type: 'icon' as const, platform: 'tiktok' as const },
  { type: 'icon' as const, platform: 'youtube' as const },
  { type: 'icon' as const, platform: 'apple' as const },
  { type: 'icon' as const, platform: 'spotify' as const },
];

// Use only base data (20 particles) for cleaner look
const particleData = baseParticleData;

const SocialIcon = ({ platform }: { platform: 'spotify' | 'instagram' | 'tiktok' | 'youtube' | 'apple' }) => {
  const icons = {
    spotify: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
      </svg>
    ),
    instagram: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    tiktok: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    apple: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
    ),
  };

  return icons[platform];
};

const getWeightedXPosition = () => {
  const random = Math.random();
  
  // 30% chance: Left zone (0-40%) - sparse, where text is
  if (random < 0.30) {
    return Math.random() * 40;
  }
  // 35% chance: Middle zone (40-70%) - moderate
  else if (random < 0.65) {
    return 40 + Math.random() * 30;
  }
  // 35% chance: Right zone (70-100%) - moderate
  else {
    return 70 + Math.random() * 30;
  }
};

const MIN_DISTANCE = 10; // 10% minimum horizontal distance for metrics
const ICON_MIN_DISTANCE = 15; // 15% minimum distance for icons

const generateParticlesWithSpacing = () => {
  const generated: Particle[] = [];
  
  particleData.forEach((data, index) => {
    let x = getWeightedXPosition();
    
    // Stratified vertical distribution - ensure even coverage across 4 zones
    const zone = index % 4;
    const zoneStart = zone * 25;
    const startY = zoneStart + (Math.random() * 25); // Random within zone
    
    // Multi-layered timing system - 3 groups for better staggering
    const timingGroup = index % 3;
    let delay: number;
    let duration: number;
    
    if (timingGroup === 0) {
      // Group 1: Quick starters with slower duration
      delay = -(Math.random() * 3); // -3 to 0s
      duration = 28 + Math.random() * 4; // 28-32s (slower)
    } else if (timingGroup === 1) {
      // Group 2: Medium delay with slower duration
      delay = -3 - (Math.random() * 3); // -6 to -3s
      duration = 32 + Math.random() * 4; // 32-36s (slower)
    } else {
      // Group 3: Late starters with much slower duration
      delay = -6 - (Math.random() * 3); // -9 to -6s
      duration = 36 + Math.random() * 8; // 36-44s (much slower)
    }
    
    let attempts = 0;
    
    const isIcon = data.type === 'icon';
    
    // Try to find a non-overlapping position (max 30 attempts for icons, 20 for metrics)
    while (attempts < (isIcon ? 30 : 20)) {
      const hasOverlap = generated.some(p => {
        const horizontalDistance = Math.abs(p.x - x);
        
        // Icon-to-icon needs more spacing
        if (isIcon && p.type === 'icon') {
          if (horizontalDistance < ICON_MIN_DISTANCE) return true;
          
          // Extra check: if similar animation timing, need even more distance
          if (Math.abs(p.delay - delay) < 2 && horizontalDistance < ICON_MIN_DISTANCE + 5) {
            return true;
          }
        }
        
        // Icon-to-metric or metric-to-metric uses standard distance
        return horizontalDistance < MIN_DISTANCE;
      });
      
      if (!hasOverlap) break;
      
      // For icons, try a completely new position on each attempt
      x = getWeightedXPosition();
      if (isIcon && attempts > 15) {
        // After 15 attempts, adjust timing to avoid temporal overlap
        delay = -(Math.random() * 6);
        duration = 16 + Math.random() * 8;
      }
      attempts++;
    }
    
    generated.push({
      id: index,
      type: data.type,
      content: data.content,
      platform: data.platform,
      x,
      startY,
      delay,
      duration,
      size: data.type === 'icon' ? 'md' : 
        (['sm', 'md', 'lg'][Math.floor(Math.random() * 3)] as 'sm' | 'md' | 'lg'),
    });
  });
  
  return generated;
};

// Generate particles once at module level (not on every render)
const PARTICLES = generateParticlesWithSpacing();

export function ParticleBackground() {
  const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
    switch (size) {
      case 'sm':
        return 'text-[10px] px-2 py-1';
      case 'md':
        return 'text-xs px-3 py-1.5';
      case 'lg':
        return 'text-sm px-3.5 py-1.5';
    }
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {PARTICLES.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-float-up"
          style={{
            left: `${particle.x}%`,
            bottom: `${particle.startY}%`,
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`,
          }}
        >
          {particle.type === 'metric' ? (
            <div
              className={`
                ${getSizeClasses(particle.size)}
                bg-accent-a/3
                border border-accent-a/8
                rounded-full
                font-mono
                text-accent-a/40
                whitespace-nowrap
              `}
            >
              {particle.content}
            </div>
          ) : (
            <div
              className="
                flex items-center justify-center
                bg-accent-a/3
                border border-accent-a/8
                rounded-full
                w-8 h-8
                text-accent-a/30
              "
            >
              <SocialIcon platform={particle.platform!} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
